import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createSalebill,
  createSaleBill,
  fetchAllSalebills,
  updateSalebill,
  updateSaleBill,
  fetchOneSalebill,
  fetchLastTraderSaleBillNumber,
  deleteSalebill,
  checkSalebillDuplicate,
  getPackingListDetails,
  fetchCndnItemRowDetails,
  generateSalebillRegisterReport,
  generateSalebillRegisterReportFull,
  generateSalebillRegisterCSV,
  generateSalebillRegisterCSVFull,
  viewSaleBillReport,
  generatePaymentDueAgingReport,
  generateAnalysisItemsReport,
  generateLedgerReport,
  generateJournalBookReport,
  generateTrialBalanceReport,
  generateBalanceSheetReport,
  generateProfitLossReport,
} from "services/salebill";

export const useAllSalebills = (filters = {}, options={}) => {
  const {enabled=true, refetchOnWindowFocus=true} = options
  const query = useQuery({
    queryKey: ["all-salebills", filters],
    queryFn: () => fetchAllSalebills(filters),
    enabled,
    refetchOnWindowFocus
  });
  return query;
};

export const useOneSalebill = (id, options = {}) => {
  const {enabled=true, refetchOnWindowFocus=true} = options;
  const query = useQuery({
    queryKey: ["one-salebill", id],
    queryFn: () => {
      if (id) {
        return fetchOneSalebill(id);
      }
    },
    enabled,
    refetchOnWindowFocus
  });
  return query;
};

export const useLastTraderSaleBillNumber = (enabled = true) => {
  return useQuery({
    queryKey: ["last-trader-sale-bill-number"],
    queryFn: fetchLastTraderSaleBillNumber,
    enabled,
  });
};

export const useCreateSalebill = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    queryKey: ["create-salebill"],
    mutationFn: (data) => createSalebill(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["all-salebills"] });
    },
  });
};

export const useUpdateSalebill = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    queryKey: ["update-salebill"],
    mutationFn: ({ id, data }) => updateSalebill(id, data),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["all-salebills"] });
      queryClient.invalidateQueries({ queryKey: ["one-salebill", variables.id] });
    },
  });
};

export const useDeleteSalebill = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    queryKey: ["delete-salebill"],
    mutationFn: (id) => deleteSalebill(id),
    onSuccess: (data, id) => {
      queryClient.invalidateQueries({ queryKey: ["all-salebills"] });
      queryClient.invalidateQueries({ queryKey: ["one-salebill", id] });
    },
  });
};

export const useCheckSalebillDuplicate = () => {
  const query = useMutation({
    mutationFn: (salebillno) => checkSalebillDuplicate(salebillno),
  });
  return query;
};

export const usePackingListDetails = (filters = {}, options = {}) => {
  const { enabled = true } = options;
  
  const query = useQuery({
    queryKey: ["packing-list-details", filters],
    queryFn: () => getPackingListDetails(filters),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
  
  return query;
};

export const useGenerateSalebillRegisterReport = () => {
  const mutation = useMutation({
    mutationFn: (reportData) => generateSalebillRegisterReport(reportData),
  });
  return mutation;
};

export const useGenerateSalebillRegisterReportFull = () => {
  const mutation = useMutation({
    mutationFn: (reportData) => generateSalebillRegisterReportFull(reportData),
  });
  return mutation;
};

export const useGenerateSalebillRegisterCSV = () => {
  const mutation = useMutation({
    mutationFn: (reportData) => generateSalebillRegisterCSV(reportData),
  });
  return mutation;
};

export const useGenerateSalebillRegisterCSVFull = () => {
  const mutation = useMutation({
    mutationFn: (reportData) => generateSalebillRegisterCSVFull(reportData),
  });
  return mutation;
};

export const useViewSaleBillReport = () => {
  const mutation = useMutation({
    mutationFn: (id) => viewSaleBillReport(id),
  });
  return mutation;
};

export const useGeneratePaymentDueAgingReport = () => {
  const mutation = useMutation({
    mutationFn: (reportData) => generatePaymentDueAgingReport(reportData),
  });
  return mutation;
};

/**
 * Comprehensive Sale Bill Creation Hook
 * Supports all bill types: DIR, STK, PL, ORD
 */
export const useCreateSaleBill = () => {
  const queryClient = useQueryClient();
  
  const mutation = useMutation({
    mutationFn: (data) => createSaleBill(data),
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ["all-salebills"] });
      queryClient.invalidateQueries({ queryKey: ["packing-list-details"] });
      queryClient.invalidateQueries({
        queryKey: ["last-trader-sale-bill-number"],
      });
    },
  });
  
  return mutation;
};

/**
 * Comprehensive Sale Bill Update Hook
 * Supports all bill types: DIR, STK, PL, ORD
 */
export const useUpdateSaleBill = () => {
  const queryClient = useQueryClient();
  
  const mutation = useMutation({
    mutationFn: ({ id, data }) => updateSaleBill(id, data),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["all-salebills"] });
      queryClient.invalidateQueries({ queryKey: ["one-salebill", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["packing-list-details"] });
    },
  });
  
  return mutation;
};

export const useCndnItemRowDetails = () => {
  return useMutation({
    mutationFn: (params) => fetchCndnItemRowDetails(params),
  });
};

export const useGenerateAnalysisItemsReport = () => {
  const mutation = useMutation({
    mutationFn: (reportData) => generateAnalysisItemsReport(reportData),
  });
  return mutation;
};

export const useGenerateLedgerReport = () => {
  const mutation = useMutation({
    mutationFn: (reportData) => generateLedgerReport(reportData),
  });
  return mutation;
};

export const useGenerateJournalBookReport = () => {
  const mutation = useMutation({
    mutationFn: (reportData) => generateJournalBookReport(reportData),
  });
  return mutation;
};

export const useGenerateTrialBalanceReport = () => {
  const mutation = useMutation({
    mutationFn: (reportData) => generateTrialBalanceReport(reportData),
  });
  return mutation;
};

export const useGenerateBalanceSheetReport = () => {
  return useMutation({
    mutationFn: () => generateBalanceSheetReport(),
  });
};

export const useGenerateProfitLossReport = () => {
  return useMutation({
    mutationFn: () => generateProfitLossReport(),
  });
};
